import axios from "axios";
import { jwtDecode } from "jwt-decode";
import React, { ReactNode, useEffect, useState } from "react";

import { Euser } from "../../types/euser.ts";
import { EuserRole } from "../../types/euserRole.ts";
import { notify } from "../../utils/notify.tsx";
import { instance, updateUser } from "../httpClient";

type DecodedJwt = {
  email: string;
  id: string;
  roles: string;
  managedSponsorIds?: string;
};

// So this is called Euser... for now or maybe forever.
// Wanted to use "user" but that is taken by actual DB record matching util.

type EuserContextType = {
  euser: Euser | null;
  // updateRole: (newRole: EuserRole) => void;
  signUpEuser?: (data: object) => Promise<void>;
  signInEuser?: (data: object) => Promise<any>;
  recoverEuser?: (data: object) => Promise<any>;
  signInWithTempAuthCode?: (tempAuthCode: string) => Promise<any>;
  signOut: () => void;
  euserIsAdmin: () => boolean;
  euserIsEditor: () => boolean;
  euserIsVolunteer: () => boolean;
};

const EuserContext = React.createContext<EuserContextType>({
  euser: null,
  euserIsAdmin: () => false,
  euserIsEditor: () => false,
  euserIsVolunteer: () => false,
  signOut: () => null,
});

type EuserProviderProps = {
  children: ReactNode;
};

const EuserProvider = ({ children }: EuserProviderProps) => {
  const [euser, setEuser] = useState(() => {
    // Check for existing euser data in local storage
    const savedEuser = sessionStorage.getItem("euser");
    return savedEuser ? JSON.parse(savedEuser) : null;
  });

  // Update local storage whenever euser changes
  useEffect(() => {
    if (euser) {
      sessionStorage.setItem("euser", JSON.stringify(euser));
    } else {
      sessionStorage.removeItem("euser");
    }
  }, [euser]);

  const signUpEuser = async (data: object) => {
    let isSignUp = false;
    try {
      const {
        nameFirst,
        nameLast,
        phone,
        email,
        password,
        passwordConfirmation,
      } = data as {
        nameFirst: string;
        nameLast: string;
        phone: string;
        email: string;
        password: string;
        passwordConfirmation: string;
      };
      // sign up new user
      await instance.post(
        `/Auth/register`,
        {
          email: email,
          password: password,
          passwordConfirmation: passwordConfirmation,
        },
        // so we can show a tailored error message
        { skipErrorToast: true }
      );
      isSignUp = true;
      // sign in new user
      const signInResponse = await signInEuser({
        email: email,
        password: password,
      });
      // get euser id since the "euser" state variable will not have the updated value in this run
      let euserId = null;
      if (signInResponse) {
        const token = signInResponse;
        const decoded = jwtDecode<DecodedJwt>(token);
        euserId = decoded.id;
      } else {
        throw new Error("Token not received");
      }
      // update info for new user so that first name, last name and phone are stored
      await updateUser({
        email: email, // an error is thrown if email is not included
        id: euserId,
        nameFirst: nameFirst,
        nameLast: nameLast,
        phone: phone,
      });
      return;
    } catch (error: any) {
      // handle email already registered error from API
      const emailErrors = (error as any).response?.data?.errors?.email;
      const helpText = `\n\nIf you need assistance, please contact us at golfforhope@hcahealthcare.com.`;

      if (
        !isSignUp &&
        emailErrors?.includes("Email address is already registered.")
      ) {
        notify.error(
          'An account with this email address already exists. Please select "Sign In" from the menu to log in or to recover your account.' +
            helpText,
          { style: { whiteSpace: "pre-line" } }
        );
        throw error;
      }

      if (isSignUp) {
        // registration succeeded but a subsequent step (sign-in or profile update) failed
        notify.warn(
          `An error occurred, but we were still able to create your account. If you see "Sign Out" on the left-hand menu then the automatic sign-in ` +
            `was successful; if not, you can sign in by selecting "Sign In" from the menu.\n\nOnce you're signed in, select "Account" from ` +
            `the menu and enter your first name, last name, and phone number if this info is not displayed.` +
            helpText,
          { style: { whiteSpace: "pre-line" } }
        );
      } else if (axios.isAxiosError(error) && error.response?.data?.errors) {
        // field validation errors: { errors: { fieldName: ["msg"] } }
        const errors = Object.values(error.response.data.errors).flat();
        notify.error(errors.join("\n\n"), {
          style: { whiteSpace: "pre-line" },
        });
      } else if (axios.isAxiosError(error) && error.response?.data?.Message) {
        notify.error(error.response.data.Message + helpText, {
          style: { whiteSpace: "pre-line" },
        });
      } else {
        notify.error(
          `An error occurred during sign up: ${error?.message || "please try again."}` +
            helpText,
          { style: { whiteSpace: "pre-line" } }
        );
      }
      throw error;
    }
  };

  const signInEuser = async (data: object) => {
    // this statement is outside of try block since request errors (i.e. axios errors) already print to the console and alert user of error (see httpClient.ts)
    const response = await instance.post(`/Auth/login`, data);
    try {
      if (response.data) {
        const token = response.data;
        processJwt(token);
      } else {
        throw new Error("Token not received");
      }
      return response.data;
    } catch (error: any) {
      console.error(error);
      notify.error(
        "An error occurred during sign in." +
          (error?.message ? `-${error.message}` : "")
      );
      throw error;
    }
  };

  const recoverEuser = async (data: object) => {
    const response = await instance.post(`/Auth/recover`, data);
    // This should always be an ok.
    // Security measure to not disclose if a user exists or not.
    return response.data;
  };

  const signInWithTempAuthCode = async (tempAuthCode: string) => {
    const requestBody = { tempAuthCode: tempAuthCode };
    const response = await instance.post(`/Auth/loginViaAuthCode`, requestBody);
    try {
      if (response.data) {
        const token = response.data;
        processJwt(token);
      } else {
        throw new Error("Token not received");
      }
      return response.data;
    } catch (error: any) {
      console.error(error);
      notify.error(
        "An error occurred during sign in." +
          (error?.message ? `-${error.message}` : "")
      );
      throw error;
    }
  };

  const processJwt = (token: string) => {
    sessionStorage.setItem("jwtToken", token);

    const decoded = jwtDecode<DecodedJwt>(token);
    const roleArray: EuserRole[] = JSON.parse(decoded.roles);
    const managedSponsorIds: number[] = decoded.managedSponsorIds
      ? JSON.parse(decoded.managedSponsorIds)
      : [];

    const our_euser: Euser = {
      email: decoded.email,
      id: decoded.id,
      roles: roleArray,
      managedSponsorIds,
      isAuthenticated: true,
    };

    setEuser(our_euser);
  };

  const signOut = () => {
    setEuser(null);
    sessionStorage.removeItem("jwtToken");
    sessionStorage.removeItem("euser"); // Clear session storage
  };

  const euserIsAdmin = () => euser?.roles.includes(EuserRole.Admin);
  const euserIsEditor = () => euser?.roles.includes(EuserRole.ContentEditor);
  const euserIsVolunteer = () => euser?.roles.includes(EuserRole.Volunteer);

  return (
    <EuserContext.Provider
      value={{
        euser,
        signInEuser,
        signUpEuser,
        recoverEuser,
        signInWithTempAuthCode,
        signOut,
        euserIsAdmin,
        euserIsEditor,
        euserIsVolunteer,
      }}
    >
      {children}
    </EuserContext.Provider>
  );
};

export { EuserContext, EuserProvider };
